import { computed, onUnmounted, reactive, ref, toRefs, unref } from "@vue/composition-api";
import {
  LiveChatJoinResponse,
  LiveChatUser,
  LivechatSubjectType,
  LiveChatUserBroadcastingEvent,
  LiveChatUserUpdateAction,
  LivechatAccessibility,
} from "@scrile/api-provider/dist/api/LivechatsProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { ErrorDTO } from "@scrile/api-provider/dist/lib/apiErrorHandler";
import useOnUserLogin from "@/hooks/useOnUserLogin";
import providers from "@/lib/api";
import { parseISO } from "@/lib/time";
import useStreamLogger from "./useStreamLogger";

interface State {
  joinData: LiveChatJoinResponse | null;
  loading: boolean;
  errorData: Record<string, any> | null;
}

type UserBroadcastingCB = (data: LiveChatUserBroadcastingEvent) => void;
type UserListUpdatedCB = (data: { action: LiveChatUserUpdateAction; user: LiveChatUser }) => void;

export default function useLiveChat({ userId, connectionDelay = 0 }: { userId: string; connectionDelay?: number }) {
  const state = reactive<State>({
    joinData: null,
    loading: false,
    errorData: null,
  });
  const token = computed(() => state.joinData?.token ?? "");
  const userList = computed<LiveChatUser[]>(() =>
    (state.joinData ? state.joinData.users : []).filter(i => i.userId !== userId)
  );
  const clientServerTimeDiff = ref(0);
  const setClientServerTimeDiff = (serverTime: Date) => {
    clientServerTimeDiff.value = new Date().getTime() - serverTime.getTime();
  };

  const updateUserList = (users: LiveChatUser[]) => {
    if (!state.joinData) return;
    state.joinData = {
      ...state.joinData,
      users,
    };
  };
  const getLoggerContext = () => {
    return {
      joinData: state.joinData,
      errorData: state.errorData,
    };
  };
  const { businessLogicLog, collectErrorLogs } = useStreamLogger("[USE LIVECHAT]", getLoggerContext);

  const endChat = async () => {
    if (!state.joinData) return;
    businessLogicLog("End chat");
    await providers.LivechatsProvider.end({ token: state.joinData.token });
    state.joinData = null;
  };

  let onUserBroadcastingCB: UserBroadcastingCB = () => {};
  let userBroadcastingUnsubscribe = () => {};
  onUnmounted(() => userBroadcastingUnsubscribe());
  const setOnUserBroadcastingCB = (cb: UserBroadcastingCB) => (onUserBroadcastingCB = cb);
  const subscribeUserBroadcasting = async () => {
    businessLogicLog("Subscribe users to the broadcasting");
    if (!token.value) throw new Error("Empty token");
    const subscription = await providers.LivechatsProvider.subscribeUserBroadcasting(
      unref(token),
      onUserBroadcastingCB
    );
    userBroadcastingUnsubscribe = () => subscription.unsubscribe();
  };

  let onUserListUpdatedCB: UserListUpdatedCB = () => {};
  let userListUpdateUnsubscribe = () => {};
  onUnmounted(() => userListUpdateUnsubscribe());
  const setOnUserListUpdatedCB = (cb: UserListUpdatedCB) => (onUserListUpdatedCB = cb);
  const subscribeUserListUpdated = async () => {
    businessLogicLog("Update subscription list of users");
    const subscription = await providers.LivechatsProvider.subscribeUserUpdate(
      unref(token),
      (data: { action: LiveChatUserUpdateAction; user: LiveChatUser }) => {
        if (!state.joinData) return;
        onUserListUpdatedCB(data);
        if (data.action === LiveChatUserUpdateAction.DELETE) {
          updateUserList(state.joinData.users.filter(i => i.id !== data.user.id));
        }
        if (data.action === LiveChatUserUpdateAction.UPDATE) {
          if (state.joinData.users.find(i => data.user.id === i.id)) {
            updateUserList(state.joinData.users.map(i => (i.id === data.user.id ? data.user : i)));
          } else updateUserList(state.joinData.users.concat([data.user]));
        }
      }
    );
    userListUpdateUnsubscribe = () => subscription.unsubscribe();
  };
  const useSubscribeAccessibilityUpdated = (
    subject: Subject<LivechatSubjectType>,
    onData: (data: LivechatAccessibility) => void
  ) => {
    let unsubscribe = () => {};
    onUnmounted(() => unsubscribe());
    providers.LivechatsProvider.subscribeAccessibilityUpdated({ subject }, onData).then(
      subscription => (unsubscribe = () => subscription.unsubscribe())
    );
  };

  const keepAliveStart = ref<boolean>(true);
  const keepAlive = async (token: string): Promise<void> => {
    await new Promise(r => setTimeout(r, 1000));
    if (!keepAliveStart.value) return;
    try {
      await providers.LivechatsProvider.keepalive({ token });
    } catch (e) {
      collectErrorLogs("Keep alive", e);
    }
    return keepAlive(token);
  };
  onUnmounted(() => (keepAliveStart.value = false));

  const subject = {
    subjectId: userId,
    subjectType: LivechatSubjectType.PUBLIC_LIVECHAT,
  };

  const onCreated = async () => {
    try {
      state.loading = true;
      await new Promise(r => setTimeout(r, connectionDelay));
      businessLogicLog("Create chat");
      state.joinData = await providers.LivechatsProvider.join({
        subject: {
          subjectId: userId,
          subjectType: LivechatSubjectType.PUBLIC_LIVECHAT,
        },
      });
      setClientServerTimeDiff(parseISO(state.joinData.serverTime));
      await subscribeUserListUpdated();
      await subscribeUserBroadcasting();
      keepAlive(state.joinData.token);
    } catch (e) {
      const error = (e as ErrorDTO[]).find(
        i => i?.details?.messageTemplate === "{com.scrile.event_posts.security.EventLiveChatSecurityProvider.message}"
      );
      if (error) {
        const err = error?.details?.data ?? "";
        state.errorData = typeof err === "string" ? JSON.parse(err) : "";
      }
      collectErrorLogs("Create chat", e);
      throw e;
    } finally {
      state.loading = false;
    }
  };
  useSubscribeAccessibilityUpdated(subject, async () => {
    await endChat();
    await onCreated();
  });
  onCreated();
  onUnmounted(endChat);
  useOnUserLogin(async () => {
    await endChat();
    await onCreated();
  });
  return {
    token,
    userList,
    ...toRefs(state),
    setOnUserListUpdatedCB,
    setOnUserBroadcastingCB,
    clientServerTimeDiff,
  };
}
